import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const labelClass = "block text-[10px] font-bold uppercase tracking-widest text-on-surface-variant mb-0.5";

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ mobile: '', password: '', role: 'customer' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [toast, setToast] = useState(null);

  const toastKey = React.useRef(0);
  const showToast = (msg, type = 'error') => {
    toastKey.current += 1;
    setToast({ msg, type, key: toastKey.current });
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reason = params.get('reason');
    if (reason === 'banned')  showToast('⛔ Your account has been suspended. Contact support.');
    if (reason === 'deleted') showToast('❌ Your account no longer exists.');
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    // Validations
    if (!formData.mobile.trim()) {
      showToast('Mobile number is required');
      return;
    }
    if (!/^[6-9]\d{9}$/.test(formData.mobile.trim())) {
      showToast('Enter a valid 10-digit Indian mobile number');
      return;
    }
    if (!formData.password) {
      showToast('Password is required');
      return;
    }
    if (formData.password.length < 6) {
      showToast('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/auth/login', formData);
      login(response.data);
      showToast('Login successful! Redirecting...', 'success');
      setTimeout(() => {
        switch (response.data.role) {
          case 'admin':   navigate('/admin'); break;
          case 'farmer':  navigate('/farmer'); break;
          case 'butcher': navigate('/butcher'); break;
          case 'customer':navigate('/customer'); break;
          default:        navigate('/');
        }
      }, 2000); // Increased to 2000ms to see success toast
    } catch (err) {
      const msg = err.response?.data?.message || 'Login failed. Please try again.';
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 font-body">
      {toast && (
        <div key={toast.key} className={`fixed top-5 left-1/2 -translate-x-1/2 z-50 shadow-lg rounded-xl overflow-hidden text-xs font-semibold min-w-[220px] ${
          toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-500 text-white'
        }`}>
          <div className="flex items-center gap-2 px-4 py-2.5">
            <span className="material-symbols-outlined" style={{fontSize:'15px'}}>
              {toast.type === 'success' ? 'check_circle' : 'error'}
            </span>
            <span className="flex-1">{toast.msg}</span>
            <button onClick={() => setToast(null)} className="opacity-70 hover:opacity-100">
              <span className="material-symbols-outlined" style={{fontSize:'13px'}}>close</span>
            </button>
          </div>
        </div>
      )}

      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          className="w-full h-full object-cover brightness-75"
          src="https://lh3.googleusercontent.com/aida-public/AB6AXuDfsl01tTn-hVDG7p-WvDCVX3XdN_BGl1NvnPvkgV5vwh0JWPoCa7GXgE6dDRnDTrNey4uw-t6iHSW5ufw7qjCw5_IMW_E6PQGHubjCpmuGL9m4uC87jkpdIE00XlDaJpJKYraCCrQJ0_LaqbKVg1qck_8PrllLS3TV3L_Aw3QkftgJIHIbK7JMD4oqLHC2JyisWL22tulMprWo3pEZ0kQBgKYMZWbk8RlMlXXjKJ7zBoEb7Iks8DnFMFjzWeYHrONEZ7a_JXv8NsCj"
          alt="" aria-hidden="true"
        />
        <div className="absolute inset-0 backdrop-blur-md bg-black/35"></div>
      </div>

      {/* Backdrop — click outside to close */}
      <div
        className="relative z-10 h-full flex items-center justify-center px-4"
        onClick={() => navigate('/')}
      >
        {/* Modal */}
        <div
          className="w-full max-w-3xl bg-white rounded-2xl overflow-hidden flex shadow-2xl"
          style={{ maxHeight: '96vh' }}
          onClick={(e) => e.stopPropagation()}
        >

          {/* Left — Image */}
          <div className="hidden md:block md:w-[52%] relative flex-shrink-0">
            <img
              alt="Champion Goat"
              className="absolute inset-0 w-full h-full object-cover object-center"
              src="https://lh3.googleusercontent.com/aida-public/AB6AXuBNZjE4zecpdmjUeAVC4rPYP7OQqi16zylXNEwpeRDGgV9CE3azczQymWXOe_4TXDs5QXqwHDYUHuq5_1M6RmGRmeXnxcoo7HxsNAxmHwRJ_26uxoRJNEo-MmVMRtaSDkBV76VXzlnMfhziQQbaMH5pAlOoGpHi66hxpY_Ehrfv6YN8uSolFNNU0goPgZB8Tq3tTLaIy1o7lxJbbHU2sG0tPz4HlEX5tLtDDKneefPgBr3QZ6ssnOIk50w7LbMNj_UX-ZVHKyUJjyGd"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent"></div>
            <div className="absolute bottom-8 left-8 right-8">
              <h2 className="font-headline text-lg font-extrabold text-white leading-tight tracking-tight mb-1.5">
                The Gold Standard of Pastoral Trade
              </h2>
              <p className="text-white/75 text-xs font-medium leading-relaxed">
                Join the most prestigious community of breeders and buyers across the subcontinent.
              </p>
            </div>
          </div>

          {/* Right — Form */}
          <div className="flex-1 flex flex-col p-5">

            {/* Close */}
            <div className="flex justify-end mb-2">
              <button onClick={() => navigate('/')}
                className="text-outline hover:text-on-surface p-1 rounded-full hover:bg-surface-container-low transition-colors">
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>close</span>
              </button>
            </div>

            {/* Logo + heading */}
            <div className="mb-4 flex flex-col items-center md:items-start">
              <img
                src="/assets/elite-goats.png"
                alt="Elite Goats & Sheeps Hub"
                className="h-8 w-auto object-contain mb-3"
              />
              <h1 className="font-headline text-base font-bold text-on-surface tracking-tight">
                Welcome Back
              </h1>
              <p className="text-on-surface-variant text-[11px]">
                Login to your marketplace account.
              </p>
            </div>

            <div className="flex flex-col gap-2.5">
              {/* Role */}
              <div>
                <label className={labelClass}>Login As</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full px-3 py-1.5 bg-surface-container-low border border-outline-variant/30 rounded-lg focus:ring-1 focus:ring-primary focus:bg-white transition-all outline-none text-xs text-on-surface"
                >
                  <option value="customer">Customer (Buyer)</option>
                  <option value="farmer">Farmer / Cattle Owner</option>
                  <option value="butcher">Butcher / Shop Owner</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {/* Mobile */}
              <div>
                <label className={labelClass}>Mobile Number</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-2.5 flex items-center pointer-events-none">
                    <span className="text-[10px] font-bold text-outline">+91</span>
                  </div>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    className="w-full pl-8 pr-3 py-1.5 bg-surface-container-low border border-outline-variant/30 rounded-lg focus:ring-1 focus:ring-primary focus:bg-white transition-all outline-none placeholder:text-outline-variant text-xs"
                    placeholder="Enter mobile number"
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className={labelClass}>Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full px-3 py-1.5 pr-8 bg-surface-container-low border border-outline-variant/30 rounded-lg focus:ring-1 focus:ring-primary focus:bg-white transition-all outline-none placeholder:text-outline-variant text-xs"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-2 flex items-center text-outline hover:text-on-surface transition-colors"
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                      {showPassword ? 'visibility_off' : 'visibility'}
                    </span>
                  </button>
                </div>
              </div>

              {/* Forgot */}
              <div className="flex justify-end -mt-1">
                <Link to="/forgot-password"
                  className="text-[11px] text-primary hover:underline underline-offset-4">
                  Forgot Password?
                </Link>
              </div>

              {/* Submit */}
              <button
                type="button"
                onClick={handleSubmit} disabled={loading}
                className="w-full py-2 bg-primary hover:bg-primary/90 text-white font-headline font-bold text-xs rounded-xl shadow-md active:scale-[0.98] transition-all duration-200 disabled:bg-outline disabled:cursor-not-allowed"
              >
                {loading ? 'Logging in...' : 'Login'}
              </button>

              {/* Register link */}
              <p className="text-center text-[11px] text-on-surface-variant">
                New seller or buyer?{' '}
                <Link to="/register"
                  className="text-primary font-bold hover:underline underline-offset-4">
                  Register Now
                </Link>
              </p>

              <div className="pt-2 border-t border-surface-container-high text-center">
                <button type="button"
                  className="text-[11px] text-secondary hover:text-primary transition-colors">
                  Need help accessing your account?
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;